import {
  AgenciesForm,
  AgenciesPage,
  ClientsPage,
  LocationsForm,
  LocationsPage,
  TerminalsForm,
  TerminalsPage,
} from "@revenue-management/pos-config-tool";

import AdminPage from "@/components/adminPage";
import CustomerSearchPage from "@/components/customerSearchPage";
import Dashboard from "@/components/dashboard";
import LoginPage from "@/components/loginPage";
import {
  PageWithCallbackCheck,
  PageWithNavigationLayout,
  PageWithNoLayout,
  PageWithSimpleLayout,
  POSConfigToolAdminPage,
} from "@/components/page/Page";
import Payment from "@/components/payment";
import ProfilePage from "@/components/profilePage";
import TransactionSearchPage from "@/components/transactionSearchPage";
import TransactionSearchPageV2Beta from "@/components/transactionSearchPageV2Beta";
import UserSettingsPage from "@/components/userSettingsPage";
import { USER_PROFILE } from "@/constants/routes";

/**
 * @typedef {boolean} Enabled - route can be enabled or disabled
 * @typedef {boolean} AdminRoute - route is for CityBase admins only
 * @typedef {boolean} ProtectedRoute - route requires authentication
 */

/**
 * A config object describing a Revenue Management route.
 *
 * @typedef {object} RouteConfig
 * @property {string} path - URL path to the route
 * @property {JSX.Element} render - component to render at this route
 * @property {Enabled} enabled
 * @property {AdminRoute} adminRoute
 * @property {ProtectedRoute} protectedRoute
 */

/**
 * A config object describing a Revenue Management router redirect.
 *
 * @typedef {object} RedirectConfig
 * @property {string} from - URL path to redirect from
 * @property {string} to - URL path to redirect to
 * @property {JSX.Element} render - component to render at this route
 * @property {Enabled} enabled
 * @property {AdminRoute} adminRoute
 * @property {ProtectedRoute} protectedRoute
 */

/**
 * Retrieves a list of filtered Revenue Management route configuration objects.
 * Use these to populate router definitions.
 *
 * @param {object?} options - parameters that effect the config objects
 * @param {boolean} options.isCustomerManagementEnabled - whether POS (aka Cashiering) features are available
 * @param {boolean} options.isLoggedIn - whether the user is logged into Revenue Management
 * @returns {RouteConfig[]} a list of Revenue Management routes
 */
export const getRoutesConfig = ({ isCustomerManagementEnabled, isLoggedIn }) => [
  {
    path: "/login",
    render: PageWithCallbackCheck(PageWithNoLayout, PageWithSimpleLayout, LoginPage),
    enabled: !isLoggedIn,
    adminRoute: false,
    protectedRoute: false,
  },
  {
    path: "/customers",
    render: PageWithNavigationLayout(CustomerSearchPage),
    enabled: isCustomerManagementEnabled,
    adminRoute: false,
    protectedRoute: true,
  },
  {
    path: "/dashboard",
    render: PageWithNavigationLayout(Dashboard),
    enabled: true,
    adminRoute: false,
    protectedRoute: true,
  },
  {
    path: "/payments",
    render: PageWithNavigationLayout(TransactionSearchPage),
    enabled: true,
    adminRoute: false,
    protectedRoute: true,
  },
  {
    // Gated by `revm-payments-beta-tab` inside the page, which redirects to
    // /payments when it's off; route config can't read LaunchDarkly.
    path: "/payments-beta",
    render: PageWithNavigationLayout(TransactionSearchPageV2Beta),
    enabled: true,
    adminRoute: false,
    protectedRoute: true,
  },
  {
    path: "/payment/:id",
    render: PageWithNavigationLayout(Payment),
    enabled: true,
    adminRoute: false,
    protectedRoute: true,
  },
  {
    path: "/settings",
    render: PageWithNavigationLayout(UserSettingsPage),
    enabled: true,
    adminRoute: false,
    protectedRoute: true,
  },
  {
    path: USER_PROFILE, // "settings/profile"
    render: PageWithNavigationLayout(ProfilePage),
    enabled: true,
    adminRoute: false,
    protectedRoute: true,
  },
  {
    path: "/admin",
    render: PageWithNavigationLayout(AdminPage, true),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
  {
    path: "/pos-config/clients/edit",
    render: POSConfigToolAdminPage(ClientsPage),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
  {
    path: "/pos-config/agencies",
    render: POSConfigToolAdminPage(AgenciesPage),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
  {
    path: "/pos-config/agencies/:id/edit",
    render: POSConfigToolAdminPage(AgenciesForm),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
  {
    path: "/pos-config/agencies/create",
    render: POSConfigToolAdminPage(AgenciesForm),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
  {
    path: "/pos-config/locations",
    render: POSConfigToolAdminPage(LocationsPage),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
  {
    path: "/pos-config/locations/:id/edit",
    render: POSConfigToolAdminPage(LocationsForm),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
  {
    path: "/pos-config/locations/create",
    render: POSConfigToolAdminPage(LocationsForm),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
  {
    path: "/pos-config/terminals",
    render: POSConfigToolAdminPage(TerminalsPage),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
  {
    path: "/pos-config/terminals/:id/edit",
    render: POSConfigToolAdminPage(TerminalsForm),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
  {
    path: "/pos-config/terminals/create",
    render: POSConfigToolAdminPage(TerminalsForm),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
];

/**
 * Retrieves a list of filtered Revenue Management redirect configuration objects.
 * Use these to populate router definitions. The definitions here should be mutually
 * exclusive so that only one catch-all redirect can be active at a time.
 *
 * @returns {RedirectConfig[]} a list of Revenue Management redirects
 */
export const getRedirectsConfig = () => [
  {
    from: "/*",
    to: "/login",
    render: PageWithSimpleLayout(LoginPage),
    enabled: true,
    adminRoute: false,
    protectedRoute: false,
  },
  {
    from: "/*",
    to: "/dashboard",
    render: PageWithNavigationLayout(Dashboard),
    enabled: true,
    adminRoute: false,
    protectedRoute: true,
  },
  {
    from: "/*",
    to: "/admin",
    render: PageWithNavigationLayout(AdminPage, true),
    enabled: true,
    adminRoute: true,
    protectedRoute: true,
  },
];
